import { Alert, Breadcrumb, Button, Card, Skeleton, Space, Tabs, Tag, Typography } from "antd";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router";
import dayjs from "dayjs";
import { api } from "../app/api.ts";
import { ApiError } from "../app/apiError.ts";
import { useAuth } from "../app/auth.tsx";
import { StudentPhoto } from "../components/StudentPhoto.tsx";
import { StudentInfoTab } from "../components/StudentInfoTab.tsx";
import { StudentStatusTab } from "../components/StudentStatusTab.tsx";
import { StudentLivingTab } from "../components/StudentLivingTab.tsx";
import { StudentDocumentsTab } from "../components/StudentDocumentsTab.tsx";
import { StudentClassTab } from "../components/StudentClassTab.tsx";
import { AuditHistory } from "../components/AuditHistory.tsx";

export function StudentDetailPage() {
  const { id = "" } = useParams();
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const canManage = hasPermission("people.manage");
  const canReadAudit = hasPermission("audit.read");

  const query = useQuery({
    queryKey: ["students", id],
    queryFn: async () => {
      const { data, error, response } = await api.GET("/students/{id}", { params: { path: { id } } });
      if (!data) throw new ApiError(response.status, error);
      return data;
    },
  });

  if (query.isPending) return <Skeleton active avatar paragraph={{ rows: 8 }} />;
  if (query.isError || !query.data) {
    const notFound = query.error instanceof ApiError && query.error.status === 404;
    return (
      <Space orientation="vertical" size="middle" style={{ width: "100%" }}>
        <Alert
          type={notFound ? "warning" : "error"}
          showIcon
          title={notFound ? t("student.notFound") : t("app.error")}
          action={notFound ? undefined : <Button onClick={() => void query.refetch()}>{t("app.retry")}</Button>}
        />
        <Link to="/families">{t("familyDetail.backToList")}</Link>
      </Space>
    );
  }

  const student = query.data;
  const fullName = [student.lastName, student.firstName, student.middleName].filter(Boolean).join(" ");
  const living = student.currentLivingOption;

  return (
    <Space orientation="vertical" size="middle" style={{ width: "100%" }}>
      <Breadcrumb
        items={[
          { title: <Link to="/families">{t("families.title")}</Link> },
          { title: <Link to={`/families/${student.familyId}`}>{student.familyName}</Link> },
          { title: `${student.lastName} ${student.firstName}` },
        ]}
      />

      <Card>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 24, alignItems: "center" }}>
          <StudentPhoto student={student} canManage={canManage} />
          <Space orientation="vertical" size={4} style={{ minWidth: 0 }}>
            <Typography.Title level={3} style={{ margin: 0 }}>
              {fullName}
            </Typography.Title>
            <Space wrap>
              <Tag color={student.status === "Active" ? "green" : "default"}>
                {student.statusChangedOn
                  ? t("student.statusSince", { status: t(`familyDetail.statuses.${student.status}`), date: dayjs(student.statusChangedOn).format("DD.MM.YYYY") })
                  : t(`familyDetail.statuses.${student.status}`)}
              </Tag>
              <Tag color={living ? "blue" : "default"}>{living ? t(`student.livingOptions.${living.option}`) : t("student.noLivingOption")}</Tag>
            </Space>
            {student.dateOfBirth && (
              <Typography.Text type="secondary">{t("familyDetail.born", { date: dayjs(student.dateOfBirth).format("DD.MM.YYYY") })}</Typography.Text>
            )}
          </Space>
        </div>
      </Card>

      <Card>
        <Tabs
          items={[
            { key: "info", label: t("student.tabs.info"), children: <StudentInfoTab student={student} canManage={canManage} /> },
            { key: "status", label: t("student.tabs.status"), children: <StudentStatusTab student={student} canManage={canManage} /> },
            { key: "living", label: t("student.tabs.living"), children: <StudentLivingTab studentId={student.id} canManage={canManage} /> },
            { key: "class", label: t("placement.tab"), children: <StudentClassTab student={student} canManage={canManage} /> },
            { key: "documents", label: t("student.tabs.documents"), children: <StudentDocumentsTab studentId={student.id} canManage={canManage} /> },
            ...(canReadAudit
              ? [{ key: "history", label: t("student.tabs.history"), children: <AuditHistory entityType="Student" entityId={student.id} includeChildren /> }]
              : []),
          ]}
        />
      </Card>
    </Space>
  );
}
